/**
 * Onglet Admin > MCP — gestion des tokens personnels MCP par avocat
 * (Sprint 3 T4, ADR-033).
 *
 * Workflow :
 * 1. Sélectionner un utilisateur (liste du tenant)
 * 2. Voir ses tokens (actifs + révoqués) avec last_used_at
 * 3. Créer un nouveau token (modal label) → modal de copie (UNE FOIS)
 * 4. Révoquer un token actif
 */
import { useCallback, useEffect, useState } from 'react';
import { IconCopy, IconKey, IconTrash } from '@tabler/icons-react';
import Btn from '../ui/Btn';
import { ApiError, api } from '../../lib/api';
import { toast } from '../../stores/toaster';

type AdminUser = {
  id: string;
  email: string;
  display_name: string | null;
  role: string;
  status: string;
};

type McpToken = {
  id: string;
  user_id: string;
  label: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  token?: string; // présent uniquement à la création
};

export default function McpTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingTokens, setLoadingTokens] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createLabel, setCreateLabel] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [revealToken, setRevealToken] = useState<McpToken | null>(null);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const rows = await api.get<AdminUser[]>('/admin/users');
      setUsers(rows);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Erreur chargement users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadTokens = useCallback(async (userId: string) => {
    setLoadingTokens(true);
    try {
      const rows = await api.get<McpToken[]>(`/admin/mcp-tokens/users/${userId}`);
      setTokens(rows);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Erreur chargement tokens');
    } finally {
      setLoadingTokens(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (selectedUserId) void loadTokens(selectedUserId);
  }, [selectedUserId, loadTokens]);

  const onCreate = async () => {
    if (!selectedUserId) return;
    setCreateBusy(true);
    try {
      const created = await api.post<McpToken>(`/admin/mcp-tokens/users/${selectedUserId}`, {
        label: createLabel.trim() || 'MCP token',
      });
      setRevealToken(created);
      setCreateLabel('');
      setCreateOpen(false);
      await loadTokens(selectedUserId);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Erreur création');
    } finally {
      setCreateBusy(false);
    }
  };

  const onRevoke = async (t: McpToken) => {
    if (!confirm(`Révoquer le token « ${t.label} » ? L'utilisateur perdra l'accès MCP.`)) return;
    try {
      await api.delete<void>(`/admin/mcp-tokens/${t.id}`);
      if (selectedUserId) await loadTokens(selectedUserId);
      toast.success('Token révoqué');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Erreur révocation');
    }
  };

  const onCopy = async (clear: string) => {
    try {
      await navigator.clipboard.writeText(clear);
      toast.success('Token copié dans le presse-papier');
    } catch {
      toast.error('Copie impossible — sélectionne manuellement');
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
      {/* Liste users */}
      <aside className="space-y-1.5">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.5px] text-text-tertiary">
          Utilisateurs ({users.length})
        </h2>
        {loadingUsers ? (
          <p className="text-[13px] text-text-secondary">Chargement…</p>
        ) : (
          users.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => setSelectedUserId(u.id)}
              className={`w-full rounded-md border p-2.5 text-left text-[12.5px] transition ${
                selectedUserId === u.id
                  ? 'border-accent bg-accent-light'
                  : 'border-rule bg-paper-elevated hover:border-rule-strong'
              }`}
            >
              <p className="truncate font-medium text-text-primary">{u.display_name || u.email}</p>
              <p className="mt-0.5 truncate text-[11px] text-text-tertiary">
                {u.email} · {u.role}
                {u.status !== 'active' && <span className="ml-1 text-cat-def">({u.status})</span>}
              </p>
            </button>
          ))
        )}
      </aside>

      {/* Détail tokens */}
      <section>
        {!selectedUserId ? (
          <div className="rounded-md border border-dashed border-rule p-6 text-center text-[13px] text-text-tertiary">
            Sélectionne un utilisateur à gauche pour voir / gérer ses tokens MCP.
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-[14px] font-medium text-text-primary">
                Tokens MCP de {users.find((u) => u.id === selectedUserId)?.display_name}
              </h2>
              <Btn type="button" variant="primary" onClick={() => setCreateOpen(true)}>
                <IconKey size={14} stroke={1.6} />
                Nouveau token
              </Btn>
            </div>

            {loadingTokens ? (
              <p className="text-[13px] text-text-secondary">Chargement…</p>
            ) : tokens.length === 0 ? (
              <p className="rounded-md border border-dashed border-rule p-6 text-center text-[13px] text-text-tertiary">
                Aucun token MCP pour cet utilisateur.
              </p>
            ) : (
              <ul className="space-y-2">
                {tokens.map((t) => (
                  <li
                    key={t.id}
                    className={`flex items-start gap-3 rounded-md border p-3 ${
                      t.revoked_at
                        ? 'border-rule bg-paper-subtle opacity-60'
                        : 'border-rule bg-paper-elevated'
                    }`}
                  >
                    <IconKey
                      size={18}
                      stroke={1.6}
                      className="mt-0.5 shrink-0 text-text-secondary"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[13px] text-text-primary">{t.label}</p>
                      <p className="mt-0.5 text-[11px] text-text-tertiary">
                        créé {new Date(t.created_at).toLocaleString('fr-BE')}
                        {t.last_used_at &&
                          ` · dernier usage ${new Date(t.last_used_at).toLocaleString('fr-BE')}`}
                        {t.revoked_at &&
                          ` · révoqué ${new Date(t.revoked_at).toLocaleString('fr-BE')}`}
                      </p>
                    </div>
                    {!t.revoked_at && (
                      <button
                        type="button"
                        onClick={() => void onRevoke(t)}
                        title="Révoquer"
                        className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-2 py-1 text-[11px] text-cat-def hover:bg-cat-def/10"
                      >
                        <IconTrash size={12} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {/* Modal création */}
      {createOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setCreateOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-md bg-paper-elevated p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-[16px] font-medium text-text-primary">
              Nouveau token MCP
            </h3>
            <p className="mt-1 text-[12px] text-text-secondary">
              Le token sera affiché UNE SEULE FOIS après création. Note-le immédiatement.
            </p>
            <label className="mt-4 block text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
              Libellé (ex. « MBP Maxim », « tablette M24 »)
            </label>
            <input
              type="text"
              value={createLabel}
              onChange={(e) => setCreateLabel(e.target.value)}
              autoFocus
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            />
            <div className="mt-5 flex justify-end gap-2">
              <Btn
                type="button"
                variant="ghost"
                onClick={() => setCreateOpen(false)}
                disabled={createBusy}
              >
                Annuler
              </Btn>
              <Btn
                type="button"
                variant="primary"
                onClick={() => void onCreate()}
                disabled={createBusy}
              >
                {createBusy ? 'Création…' : 'Créer'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Modal révélation token */}
      {revealToken?.token && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-md bg-paper-elevated p-5 shadow-xl">
            <h3 className="font-serif text-[16px] font-medium text-text-primary">
              Token créé — copie-le maintenant
            </h3>
            <p className="mt-1 text-[12px] text-cat-imp-text">
              ⚠ Ce token ne sera plus jamais affiché. Si tu le perds, génère-en un nouveau.
            </p>
            <div className="mt-4 flex items-center gap-2 rounded-md border border-rule bg-paper-subtle p-2.5">
              <code className="flex-1 truncate font-mono text-[12px]">{revealToken.token}</code>
              <button
                type="button"
                onClick={() => void onCopy(revealToken.token!)}
                className="rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[11px] hover:bg-paper-card"
              >
                <IconCopy size={12} stroke={1.6} className="-mt-0.5 mr-1 inline" />
                Copier
              </button>
            </div>
            <p className="mt-3 text-[11px] text-text-tertiary">
              À coller dans la configuration Claude.ai (connecteur MCP custom). Détails dans le
              README du repo <code className="font-mono">mcp-adr/</code>.
            </p>
            <div className="mt-5 flex justify-end">
              <Btn type="button" variant="primary" onClick={() => setRevealToken(null)}>
                J'ai noté le token
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
